import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Settings, Save, RefreshCw, Database, DollarSign, Globe } from 'lucide-react';
import { toast } from 'sonner';

const Configuration = () => {
  const [saving, setSaving] = useState(false);
  
  const handleSave = async () => {
    setSaving(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setSaving(false);
    toast.success('Configuration saved successfully');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Configuration</h1>
          <p className="text-muted-foreground mt-1">
            System settings and rate configurations
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <div className="animate-spin h-4 w-4 mr-2 border-2 border-primary-foreground border-t-transparent rounded-full" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      <Tabs defaultValue="general" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="rates">Zone Rates</TabsTrigger>
          <TabsTrigger value="currency">Currency</TabsTrigger>
          <TabsTrigger value="cache">Cache</TabsTrigger>
        </TabsList>

        {/* General Settings */}
        <TabsContent value="general" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Provider Settings</CardTitle>
              <CardDescription>Configure shipping rate providers</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* DHL Settings */}
              <div className="space-y-4 p-4 border border-border rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold">DHL Express</h4>
                    <p className="text-sm text-muted-foreground">MyDHL API configuration</p>
                  </div>
                  <Switch defaultChecked />
                </div>
                <div className="grid gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="dhl-username">API Username</Label>
                    <Input id="dhl-username" type="text" placeholder="Enter DHL API username" />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="dhl-key">API Key</Label>
                    <Input id="dhl-key" type="password" placeholder="Enter DHL API key" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="dhl-timeout">Timeout (ms)</Label>
                      <Input id="dhl-timeout" type="number" defaultValue="5000" />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="dhl-retries">Retry Attempts</Label>
                      <Input id="dhl-retries" type="number" defaultValue="3" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Turn14 Settings */}
              <div className="space-y-4 p-4 border border-border rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold">Turn14 (DHtoH)</h4>
                    <p className="text-sm text-muted-foreground">Turn14 distribution integration</p>
                  </div>
                  <Switch defaultChecked />
                </div>
                <div className="grid gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="turn14-url">API Endpoint</Label>
                    <Input id="turn14-url" type="url" placeholder="https://api.turn14.com/..." />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="turn14-timeout">Timeout (ms)</Label>
                      <Input id="turn14-timeout" type="number" defaultValue="5000" />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="turn14-retries">Retry Attempts</Label>
                      <Input id="turn14-retries" type="number" defaultValue="3" />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>System Settings</CardTitle>
              <CardDescription>General system configuration</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="circuit-breaker">Circuit Breaker</Label>
                  <p className="text-sm text-muted-foreground">Enable automatic circuit breaking on provider failures</p>
                </div>
                <Switch id="circuit-breaker" defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="arabic-detection">Arabic Address Detection</Label>
                  <p className="text-sm text-muted-foreground">Automatically detect and handle Arabic addresses</p>
                </div>
                <Switch id="arabic-detection" defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="logging">Detailed Logging</Label>
                  <p className="text-sm text-muted-foreground">Log all requests and responses for debugging</p>
                </div>
                <Switch id="logging" defaultChecked />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Zone Rates */}
        <TabsContent value="rates" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Zone Fallback Rates (SAR)</CardTitle>
                  <CardDescription>Configure backup rates when providers are unavailable</CardDescription>
                </div>
                <Button variant="outline" size="sm">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Add Rate
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Country</TableHead>
                    <TableHead>Max Weight (lb)</TableHead>
                    <TableHead>Service Level</TableHead>
                    <TableHead>Price (SAR)</TableHead>
                    <TableHead>Transit Days</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[
                    { id: 'zone-1', country: 'SA', weight: 10, service: 'Standard', price: 225.00, days: '3-5', active: true },
                    { id: 'zone-2', country: 'SA', weight: 25, service: 'Standard', price: 385.00, days: '3-5', active: true },
                    { id: 'zone-3', country: 'SA', weight: 50, service: 'Standard', price: 625.00, days: '3-5', active: true },
                    { id: 'zone-4', country: 'SA', weight: 10, service: 'Express', price: 425.00, days: '2-3', active: true },
                    { id: 'zone-5', country: 'SA', weight: 25, service: 'Express', price: 685.00, days: '2-3', active: true },
                  ].map((rate) => (
                    <TableRow key={rate.id}>
                      <TableCell className="font-medium">{rate.country}</TableCell>
                      <TableCell>{rate.weight} lb</TableCell>
                      <TableCell>
                        <Badge variant="outline">{rate.service}</Badge>
                      </TableCell>
                      <TableCell className="font-mono">{rate.price.toFixed(2)}</TableCell>
                      <TableCell>{rate.days}</TableCell>
                      <TableCell>
                        {rate.active ? (
                          <Badge variant="outline" className="bg-success-light text-success border-success/20">
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="outline">
                            Inactive
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm">Edit</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Pricing Configuration</CardTitle>
              <CardDescription>Markup and handling fees</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="markup">Markup Percentage (%)</Label>
                  <Input id="markup" type="number" defaultValue="15" step="0.1" />
                  <p className="text-xs text-muted-foreground">Applied to base shipping rates</p>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="handling">Handling Fee (SAR)</Label>
                  <Input id="handling" type="number" defaultValue="25.00" step="0.01" />
                  <p className="text-xs text-muted-foreground">Fixed fee per order</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Currency Settings */}
        <TabsContent value="currency" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                FX Rate Configuration
              </CardTitle>
              <CardDescription>Currency exchange settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="fx-provider">FX Provider</Label>
                  <Input id="fx-provider" type="text" defaultValue="fixer.io" readOnly />
                  <p className="text-xs text-muted-foreground">Currency exchange rate provider</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="fx-cache">Cache TTL (hours)</Label>
                    <Input id="fx-cache" type="number" defaultValue="24" />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="fx-buffer">Buffer (%)</Label>
                    <Input id="fx-buffer" type="number" defaultValue="0.5" step="0.1" />
                  </div>
                </div>
              </div>

              <div className="p-4 border border-border rounded-lg bg-accent/50">
                <h4 className="font-semibold mb-3">Current FX Rates</h4>
                <div className="space-y-2">
                  {[
                    { id: 'fx-1', from: 'USD', to: 'SAR', rate: 3.7525, lastUpdate: '2 hours ago' },
                    { id: 'fx-2', from: 'EUR', to: 'SAR', rate: 4.1234, lastUpdate: '2 hours ago' },
                  ].map((fx) => (
                    <div key={fx.id} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{fx.from} → {fx.to}</Badge>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="font-mono font-semibold">{fx.rate.toFixed(4)}</span>
                        <span className="text-muted-foreground text-xs">{fx.lastUpdate}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Fallback Rates</CardTitle>
              <CardDescription>Last-known-good rates when FX provider is unavailable</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { id: 'rate-1', currency: 'USD', rate: 3.7525, date: 'Apr 15, 2026' },
                  { id: 'rate-2', currency: 'EUR', rate: 4.1234, date: 'Apr 15, 2026' },
                ].map((fallback) => (
                  <div key={fallback.id} className="flex items-center justify-between p-3 border border-border rounded-lg">
                    <div>
                      <p className="font-medium">{fallback.currency} to SAR</p>
                      <p className="text-xs text-muted-foreground">Last updated: {fallback.date}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-mono font-bold">{fallback.rate.toFixed(4)}</p>
                      <Button variant="ghost" size="sm" className="mt-1">Update</Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cache Settings */}
        <TabsContent value="cache" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Cache Configuration
              </CardTitle>
              <CardDescription>Redis cache settings and management</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="cache-ttl">Rate Cache TTL (minutes)</Label>
                    <Input id="cache-ttl" type="number" defaultValue="15" />
                    <p className="text-xs text-muted-foreground">Fresh cache duration</p>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="cache-stale">Stale TTL (hours)</Label>
                    <Input id="cache-stale" type="number" defaultValue="2" />
                    <p className="text-xs text-muted-foreground">Stale-but-usable duration</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="lock-ttl">Lock TTL (seconds)</Label>
                    <Input id="lock-ttl" type="number" defaultValue="15" />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="neg-cache">Negative Cache (seconds)</Label>
                    <Input id="neg-cache" type="number" defaultValue="60" />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 border border-border rounded-lg bg-accent/50">
                <div>
                  <p className="font-semibold">Current Cache Status</p>
                  <p className="text-sm text-muted-foreground">Redis connection: Active</p>
                </div>
                <Badge variant="outline" className="bg-success-light text-success border-success/20">
                  Healthy
                </Badge>
              </div>

              <div className="space-y-3">
                <Button variant="outline" className="w-full">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Clear All Caches
                </Button>
                <Button variant="outline" className="w-full">
                  <Database className="h-4 w-4 mr-2" />
                  View Cache Statistics
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Configuration;
